import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import reckittIcon from "../../assets/images/reckitt-logo.png";
import { apiURL } from "../../shared/env";
import { useAppMe } from "../../services/okta";
import "./Login.scss";

/**
 * Login Page Component.
 *
 * This component handles the user login interface and authentication flow.
 *
 * Key features:
 * 1. **Authentication Check**: Uses `useAppMe` to check if the user is already authenticated.
 *    - If authenticated, redirects to the `redirectUrl` stored in sessionStorage (or "/" by default).
 *    - Clears the `redirectUrl` from storage after redirection.
 * 2. **SSO Login**: Provides a button to initiate the Okta SSO login process via `${apiURL}/auth/login`.
 * 3. **Error Handling**: Displays error messages if the URL contains `error` or `error_description` query parameters (e.g., after a failed login attempt or insufficient permissions).
 * 4. **Logout Message**: Displays a logout confirmation if the `logout` query parameter is present.
 * 5. **Access Request**: Provides links to request access (via ServiceNow) or request a demo (via SharePoint).
 *
 * @returns {JSX.Element} The rendered login page component.
 */
export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: dataAppMe } = useAppMe({
    options: { retry: false }
  });

  // Effect to handle automatic redirect if user is already logged in
  useEffect(() => {
    if (dataAppMe?.data?.user_id) {
      const redirectUrl = sessionStorage.getItem("redirectUrl") || "/";
      if (redirectUrl.includes("/login")) {
        navigate("/", { replace: true });
      } else {
        navigate(redirectUrl, { replace: true });
      }
      sessionStorage.removeItem("redirectUrl");
    }
  }, [dataAppMe, navigate]);

  const searchParams = new URLSearchParams(location.search);
  const error = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");
  const logout = searchParams.get("logout");

  /**
   * Redirects the user to the backend authentication endpoint to start the SSO flow.
   */
  const handleLoginOkta = () => {
    const loginUrl = `${apiURL}/auth/login`;
    window.location.href = loginUrl;
  };

  /**
   * Redirects the user to the ServiceNow portal to request access to the application.
   */
  const handleRequestAccess = () => {
    const pageUrl = `https://rbassist.service-now.com/ssp?id=sc_cat_item&sys_id=59508e6183453214b7b671dfeeaad3da&table=sc_cat_item`;
    window.location.href = pageUrl;
  };

  console.warn("Error:", error, "Error Description:", errorDescription);

  return (
    <>
      <div className="flex min-h-full flex-col justify-center px-6 container-login">
        <div className="wrapper-login">
          <div className="logo-login">
            <img src={reckittIcon} alt="Reckitt" className="mx-auto" />
          </div>
          <div className="text-center copy">
            <p>Composable Consumer Data Platform</p>
            <h2 className="text-center">
              Unifying consumer data. <br />
              Unlocking potential.
            </h2>
          </div>
          <div className="login-button-wrapper">
            {logout && (
              <div className="error-message text-center">
                <p>You have been logged out</p>
              </div>
            )}
            <button
              onClick={handleLoginOkta}
              type="button">
              Sign in with SSO
            </button>
            {error && (
              <div className="error-message text-center ">
                <p>You don’t have access</p>
              </div>
            )}
            <button
            className="request-access-button"
              onClick={handleRequestAccess}
              type="button">
              Request an access
            </button>
          </div>
          <div className="request-access-wrapper text-center">
              <p><a href="https://rbcom.sharepoint.com/sites/ITDProductCatalogue/SitePages/CCDP%20Composable%20Consumer%20Data%20Platform.aspx?stream=Marketing" target="_blank" rel="noopener noreferrer">Request a demo</a></p>
          </div>
        </div>
      </div>
    </>
  );
}
